/**
 * GuestSignupPrompt — Iter 51 full-screen 6th-scan gate.
 *
 * TWO VARIANTS (chosen from props):
 *   standard : localStorage still has the 5 pending scans → show the
 *              5 thumbnails, headline "Save your 5 scans".
 *   softer   : server_count > localStorage count (user cleared storage
 *              between sessions) → we CAN'T show thumbnails they don't
 *              remember; use the softer copy per the Iter 51 spec.
 *
 * Both variants embed RegistrationSheet on tap. A "Continue with Google"
 * button uses the existing /api/auth/google/login redirect flow — fast
 * signup for the highest-intent moment in the funnel.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import RegistrationSheet from "../../components/common/RegistrationSheet";
import { trackGuest } from "../../store/guestStore";

export default function GuestSignupPrompt({
  variant = "standard",     // "standard" | "softer"
  scans = [],
  onDismiss,
}) {
  const [openSheet, setOpenSheet] = useState(false);

  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  const startGoogle = () => {
    try {
      trackGuest("signup_google_clicked", { context: "6th_scan_gate", variant });
    } catch { /* noop */ }
    window.location.href = `${backend}/api/auth/google/login`;
  };

  const openEmail = () => {
    try {
      trackGuest("signup_shown", { context: "6th_scan_gate", variant });
    } catch { /* noop */ }
    setOpenSheet(true);
  };

  const isSoft = variant === "softer";
  const totals = scans.reduce(
    (acc, s) => ({
      calories: acc.calories + (Number(s.calories) || 0),
      protein_g: acc.protein_g + (Number(s.protein_g) || 0),
    }),
    { calories: 0, protein_g: 0 },
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[70] overflow-y-auto"
      style={{ background: "#0F0F10" }}
      data-testid={`signup-prompt-${variant}`}
    >
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-24">
        <button
          type="button"
          onClick={onDismiss}
          data-testid="signup-prompt-close"
          aria-label="Close"
          className="w-9 h-9 rounded-full grid place-items-center"
          style={{ background: "rgba(255,255,255,0.08)" }}
        >
          <X size={16} color="#fff" />
        </button>

        <div className="mt-6" data-testid="signup-prompt-headline">
          <h1
            className="font-barlow-cond"
            style={{
              fontSize: 34,
              fontWeight: 900,
              color: "#FFFFFF",
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
            }}
          >
            {isSoft
              ? "Looks like you've scanned with us before"
              : `Save your ${scans.length} scans`}
          </h1>
          <p
            className="mt-2 text-[13px]"
            style={{ color: "rgba(255,255,255,0.72)", maxWidth: 340 }}
          >
            {isSoft
              ? "Create an account to save your progress and keep scanning."
              : `You've used your 5 free scans. Create a free account to keep every meal — plus unlock unlimited scans, FitPoints, and a personal FitScore.`}
          </p>
        </div>

        {/* Thumbnails — STANDARD variant only */}
        {!isSoft && scans.length > 0 && (
          <div
            className="mt-5 rounded-[16px] p-3"
            data-testid="signup-prompt-scans"
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <div className="grid grid-cols-5 gap-2">
              {scans.slice(0, 5).map((s, i) => (
                <div
                  key={s.client_scan_id || i}
                  data-testid={`signup-thumb-${i}`}
                  className="rounded-[10px] overflow-hidden"
                  style={{
                    background: s.thumbnail_base64
                      ? `center/cover no-repeat url("${s.thumbnail_base64}")`
                      : "linear-gradient(135deg,#2a2a2c,#1a1a1c)",
                    aspectRatio: "1 / 1",
                  }}
                >
                  <div
                    className="w-full h-full flex items-end p-1.5"
                    style={{
                      background:
                        "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.75))",
                    }}
                  >
                    <div
                      className="text-[9px] font-semibold truncate w-full"
                      style={{ color: "#fff" }}
                    >
                      {s.dish_name || "Scan"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div
              className="mt-3 flex items-center justify-between text-[12px]"
              style={{ color: "rgba(255,255,255,0.80)" }}
            >
              <span>Total (post-portion)</span>
              <span data-testid="signup-prompt-totals">
                <strong style={{ color: "#fff" }}>
                  {Math.round(totals.calories)} kcal
                </strong>
                <span style={{ opacity: 0.6 }}> · {Math.round(totals.protein_g)}g protein</span>
              </span>
            </div>
          </div>
        )}

        {/* CTAs */}
        <div className="mt-6 space-y-2.5">
          <button
            type="button"
            onClick={startGoogle}
            data-testid="signup-google"
            className="w-full h-12 rounded-pill flex items-center justify-center gap-2 text-[14px]"
            style={{
              background: "#FFFFFF",
              color: "#1A1A1A",
              fontWeight: 700,
              borderRadius: 100,
            }}
          >
            <GoogleG />
            Continue with Google
          </button>
          <button
            type="button"
            onClick={openEmail}
            data-testid="signup-email"
            className="w-full h-12 rounded-pill text-[14px]"
            style={{
              background: "#00D4FF",
              color: "#000",
              fontWeight: 700,
              borderRadius: 100,
            }}
          >
            Sign up with email
          </button>
          <button
            type="button"
            onClick={() => {
              setOpenSheet(true);
              // RegistrationSheet's mode toggle handles login
            }}
            data-testid="signup-existing-account"
            className="w-full h-11 text-[13px]"
            style={{ color: "rgba(255,255,255,0.72)" }}
          >
            I already have an account · Log in
          </button>
        </div>

        {isSoft && (
          <p
            className="mt-5 text-[11px]"
            style={{ color: "rgba(255,255,255,0.48)" }}
            data-testid="signup-softer-footnote"
          >
            We recognised your device from a previous session. Your prior scans
            weren&apos;t saved because you weren&apos;t signed in — but signing up now
            unlocks unlimited scans going forward.
          </p>
        )}
      </div>

      {openSheet && (
        <RegistrationSheet
          context="save_scan"
          onSuccess={() => {
            setOpenSheet(false);
            onDismiss?.({ success: true });
          }}
          onDismiss={() => setOpenSheet(false)}
        />
      )}
    </motion.div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.4 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.6 34.4 26.9 35.6 24 35.6c-5.2 0-9.7-3.3-11.3-8L6.1 33C9.3 39.4 16.1 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.6 5.6c-.5.4 7.1-5.2 7.1-15.1 0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
